// =============================================================================
// EFI FORK (2026-07-20, spike S5) -- upstream file REPLACED.
//
// Upstream persisted collaborative scenes to EXCALIDRAW'S OWN public Firebase
// project, so a stock self-hosted build still shipped board contents to a third
// party. This module keeps the exact same export surface but stores scenes on
// OUR box, via boardstore.cgi at /store on this same origin.
//
// The privacy property is unchanged and is the whole point: elements are
// encrypted HERE, client-side, with the room key (which lives only in the URL
// fragment and never reaches any server). The server stores an opaque
// {sceneVersion, ciphertext, iv} keyed by roomId and cannot read a board.
//
// The one real semantic we must reproduce by hand: Firestore's runTransaction
// did read -> decrypt -> reconcile -> re-encrypt -> write ATOMICALLY, retrying
// on conflict. Our server cannot decrypt, so reconciliation happens here and
// the server does a locked compare-and-set, returning 409 + the newer scene.
// saveToFirebase therefore RETRIES against that scene. Without the retry, a
// losing writer silently drops its own elements.
//
// File names are kept (firebase.ts, saveToFirebase, ...) deliberately: the three
// consumers -- collab/Collab.tsx, App.tsx, components/ExportToExcalidrawPlus.tsx
// -- import these names, so keeping them makes this a ONE-FILE fork with no
// import churn. See /srv/collab/excalidraw/FORK_NOTES.md.
// =============================================================================
import { reconcileElements } from "@excalidraw/excalidraw";
import { MIME_TYPES, toBrandedType } from "@excalidraw/common";
import { decompressData } from "@excalidraw/excalidraw/data/encode";
import {
  encryptData,
  decryptData,
} from "@excalidraw/excalidraw/data/encryption";
import { restoreElements } from "@excalidraw/excalidraw/data/restore";
import { getSceneVersion } from "@excalidraw/element";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type {
  ExcalidrawElement,
  FileId,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

import { getSyncableElements } from ".";

import type { SyncableExcalidrawElement } from ".";
import type Portal from "../collab/Portal";
import type { Socket } from "socket.io-client";

// Same origin as the app (board.emergentfrontiers.com) -- see the vhost.
const STORE_BASE = "/store";
const SAVE_MAX_ATTEMPTS = 4;

// EFI FORK (capability, 2026-07-22, collab B3/D-C2): boardstore.cgi now requires
// a signed capability minted by the embedding app (forum.cgi / hub.cgi). Our app
// loads this iframe with `?cap=<capability>` in the URL; we forward it as the
// X-Board-Cap header on every /store request. Without a valid, room-scoped,
// unexpired capability the store answers 403. See FORK_NOTES.md (Patch 4).
const BOARD_CAP = new URLSearchParams(window.location.search).get("cap") || "";
const capHeaders = (extra?: Record<string, string>): Record<string, string> => ({
  ...(extra || {}),
  "X-Board-Cap": BOARD_CAP,
});

// -----------------------------------------------------------------------------
// transport helpers: ciphertext/iv travel as base64 in JSON
// -----------------------------------------------------------------------------

const toB64 = (bytes: Uint8Array): string => {
  let s = "";
  for (let i = 0; i < bytes.length; i++) {
    s += String.fromCharCode(bytes[i]);
  }
  return btoa(s);
};

const fromB64 = (b64: string): Uint8Array => {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) {
    out[i] = s.charCodeAt(i);
  }
  return out;
};

type StoredScene = {
  sceneVersion: number;
  ciphertext: string; // base64
  iv: string; // base64
};

const encryptElements = async (
  key: string,
  elements: readonly ExcalidrawElement[],
): Promise<{ ciphertext: ArrayBuffer; iv: Uint8Array }> => {
  const json = JSON.stringify(elements);
  const encoded = new TextEncoder().encode(json);
  const { encryptedBuffer, iv } = await encryptData(key, encoded);

  return { ciphertext: encryptedBuffer, iv };
};

const decryptElements = async (
  data: StoredScene,
  roomKey: string,
): Promise<readonly ExcalidrawElement[]> => {
  const ciphertext = fromB64(data.ciphertext) as Uint8Array<ArrayBuffer>;
  const iv = fromB64(data.iv) as Uint8Array<ArrayBuffer>;

  const decrypted = await decryptData(iv, ciphertext, roomKey);
  const decodedData = new TextDecoder("utf-8").decode(
    new Uint8Array(decrypted),
  );
  return JSON.parse(decodedData);
};

const createSceneDocument = async (
  elements: readonly SyncableExcalidrawElement[],
  roomKey: string,
): Promise<StoredScene> => {
  const sceneVersion = getSceneVersion(elements);
  const { ciphertext, iv } = await encryptElements(roomKey, elements);
  return {
    sceneVersion,
    ciphertext: toB64(new Uint8Array(ciphertext)),
    iv: toB64(iv),
  };
};

class SceneVersionCache {
  private static cache = new WeakMap<Socket, number>();
  static get = (socket: Socket) => {
    return SceneVersionCache.cache.get(socket);
  };
  static set = (
    socket: Socket,
    elements: readonly SyncableExcalidrawElement[],
  ) => {
    SceneVersionCache.cache.set(socket, getSceneVersion(elements));
  };
}

// -----------------------------------------------------------------------------

// Upstream returned a Firebase Storage handle here; only ExportToExcalidrawPlus
// uses it, and that feature exports to Excalidraw's hosted product, which a
// self-hosted instance has no business doing. Fail loudly rather than silently.
export const loadFirebaseStorage = async () => {
  throw new Error(
    "Excalidraw+ export is disabled on this self-hosted instance (no Firebase).",
  );
};

export const isSavedToFirebase = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
): boolean => {
  if (portal.socket && portal.roomId && portal.roomKey) {
    const sceneVersion = getSceneVersion(elements);

    return SceneVersionCache.get(portal.socket) === sceneVersion;
  }
  // if no room exists, consider the room saved so that we don't unnecessarily
  // prevent unload (there's nothing we could do at that point anyway)
  return true;
};

const fetchStoredScene = async (roomId: string): Promise<StoredScene | null> => {
  const res = await fetch(`${STORE_BASE}/scene/${encodeURIComponent(roomId)}`, {
    headers: capHeaders(),
  });
  if (res.status === 404) {
    return null;
  }
  if (!res.ok) {
    throw new Error(`scene GET failed: ${res.status}`);
  }
  return (await res.json()) as StoredScene;
};

export const saveFilesToFirebase = async ({
  prefix,
  files,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
}) => {
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        const res = await fetch(
          `${STORE_BASE}/file?prefix=${encodeURIComponent(
            prefix,
          )}&id=${encodeURIComponent(id)}`,
          {
            method: "PUT",
            headers: capHeaders({ "Content-Type": "application/octet-stream" }),
            body: buffer as BodyInit,
          },
        );
        if (!res.ok) {
          throw new Error(`file PUT failed: ${res.status}`);
        }
        savedFiles.push(id);
      } catch (error: any) {
        erroredFiles.push(id);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

export const saveToFirebase = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  appState: AppState,
) => {
  const { roomId, roomKey, socket } = portal;
  if (
    // bail if no room exists as there's nothing we can do at this point
    !roomId ||
    !roomKey ||
    !socket ||
    isSavedToFirebase(portal, elements)
  ) {
    return null;
  }

  // Replaces Firestore's runTransaction: reconcile locally, compare-and-set
  // server-side, and retry against whatever the server says is current.
  let attempt = 0;
  let prevStoredScene = await fetchStoredScene(roomId);

  // eslint-disable-next-line no-constant-condition
  while (true) {
    attempt++;

    let toStore: StoredScene;
    if (!prevStoredScene) {
      toStore = await createSceneDocument(elements, roomKey);
    } else {
      const prevStoredElements = getSyncableElements(
        restoreElements(await decryptElements(prevStoredScene, roomKey), null),
      );
      const reconciledElements = getSyncableElements(
        reconcileElements(
          elements,
          prevStoredElements as OrderedExcalidrawElement[] as RemoteExcalidrawElement[],
          appState,
        ),
      );
      toStore = await createSceneDocument(reconciledElements, roomKey);
    }

    const res = await fetch(
      `${STORE_BASE}/scene/${encodeURIComponent(roomId)}`,
      {
        method: "PUT",
        headers: capHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(toStore),
      },
    );

    if (res.status === 409) {
      // someone else committed a newer scene; reconcile against it and retry
      prevStoredScene = (await res.json()) as StoredScene;
      if (attempt >= SAVE_MAX_ATTEMPTS) {
        throw new Error("scene PUT conflicted repeatedly");
      }
      continue;
    }
    if (!res.ok) {
      throw new Error(`scene PUT failed: ${res.status}`);
    }

    const storedScene = (await res.json()) as StoredScene;
    const storedElements = getSyncableElements(
      restoreElements(await decryptElements(storedScene, roomKey), null),
    );

    SceneVersionCache.set(socket, storedElements);

    return toBrandedType<RemoteExcalidrawElement[]>(storedElements);
  }
};

export const loadFromFirebase = async (
  roomId: string,
  roomKey: string,
  socket: Socket | null,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  const storedScene = await fetchStoredScene(roomId);
  if (!storedScene) {
    return null;
  }
  const elements = getSyncableElements(
    restoreElements(await decryptElements(storedScene, roomKey), null, {
      deleteInvisibleElements: true,
    }),
  );

  if (socket) {
    SceneVersionCache.set(socket, elements);
  }

  return elements;
};

export const loadFilesFromFirebase = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        const response = await fetch(
          `${STORE_BASE}/file?prefix=${encodeURIComponent(
            prefix,
          )}&id=${encodeURIComponent(id)}`,
          { headers: capHeaders() },
        );
        if (response.status < 400) {
          const arrayBuffer = await response.arrayBuffer();

          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            {
              decryptionKey,
            },
          );

          const dataURL = new TextDecoder().decode(data) as DataURL;

          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL,
            created: metadata?.created || Date.now(),
            lastRetrieved: metadata?.created || Date.now(),
          });
        } else {
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        console.error(error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};
